/**
 * ================================================================
 * REDUX SLICE: Financial Year Management
 * ================================================================
 *
 * @fileOverview     Redux slice for centralized financial year state management
 * @module           store/slices/financialYearSlice
 * @version          1.0.0
 * @since            2025-07-30
 * @author           Development Team <dev@company.com>
 * @lastModified     2025-07-30
 *
 * @purpose          Manages financial year selection across the entire application
 * @dependencies     Redux Toolkit for state management
 * @exports          financialYearSlice, actions, and selectors
 *
 * @architecture
 * - Redux slice pattern with RTK
 * - Centralized state management
 * - Dynamic financial year generation
 *
 * @performance
 * - Minimal state updates
 * - Efficient action dispatching
 * - Memoized selectors
 *
 * @security         No sensitive data in financial year state
 * @accessibility    Financial year changes trigger screen reader announcements
 * @testing          Unit tests for all actions and reducers
 *
 * @relatedFiles
 * - /src/hooks/useFinancialYear.ts - Custom hook for financial year
 * - /src/lib/utils/dateUtils.ts - Date utility functions
 *
 * ================================================================
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  formatDateForAPI,
  getCurrentFinancialYear,
  getFinancialYearDateRange,
} from '@/lib/utils/dateUtils';

// ================================================================
// TYPE DEFINITIONS
// ================================================================

/**
 * Date range for financial year calculations
 *
 * @interface DateRange
 * @property {string} fromDate - Start date in API format (YYYY-MM-DD)
 * @property {string} toDate - End date in API format (YYYY-MM-DD)
 */
export interface DateRange {
  fromDate: string;
  toDate: string;
}

/**
 * Enhanced financial year state interface
 *
 * @interface FinancialYearState
 * @property {string} selectedFinancialYear - Display format: "2024-2025"
 * @property {DateRange} currentDateRange - Date range for selected FY
 * @property {string} defaultToDate - Default toDate based on FY (current date for current FY, end date for historical)
 * @property {boolean} isCurrentFY - Whether the selected FY is the current financial year
 * @property {string} financialYear - API format: "2024"
 * @property {string[]} availableFinancialYears - All available years
 */
export interface FinancialYearState {
  /** Current selected financial year for UI display (format: "2024-2025") */
  selectedFinancialYear: string;
  /** Financial year for API calls (format: "2024") */
  financialYear: string;
  /** All available financial years for selection */
  availableFinancialYears: string[];
  /** Date range for the selected financial year */
  currentDateRange: DateRange;
  /** Default toDate based on whether FY is current or historical */
  defaultToDate: string;
  /** Whether the selected FY is the current financial year */
  isCurrentFY: boolean;
}

// ================================================================
// UTILITY FUNCTIONS
// ================================================================

/**
 * Generate available financial years dynamically from 2021 to current year
 * Ensures the latest financial year is always included
 *
 * @returns {string[]} Array of financial years in "YYYY-YYYY" format
 * @example
 * ```typescript
 * generateFinancialYears()
 * // Returns: ["2026-2027", "2025-2026", "2024-2025", "2023-2024", "2022-2023", "2021-2022"]
 * ```
 *
 * @since 1.0.0
 */
function generateFinancialYears(): string[] {
  const years: string[] = [];
  const startYear = 2021;
  const currentYear = new Date().getFullYear();
  const endYear = currentYear; // Dynamically set to current year

  // Generate years in descending order (latest first)
  for (let year = endYear; year >= startYear; year--) {
    years.push(`${year}-${year + 1}`);
  }

  return years;
}

/**
 * Extract API-formatted year from display format
 *
 * @param {string} selectedYear - Financial year in "YYYY-YYYY" format
 * @returns {string} First year for API calls
 * @example
 * ```typescript
 * extractFinancialYear("2024-2025") // Returns: "2024"
 * ```
 *
 * @since 1.0.0
 */
function extractFinancialYear(selectedYear: string): string {
  return selectedYear.split('-')[0];
}

/**
 * Calculate date range and default toDate for a financial year
 *
 * @param {string} selectedYear - Financial year in "YYYY-YYYY" format
 * @returns {object} Date calculations for the financial year
 */
function calculateFinancialYearData(selectedYear: string) {
  const fyStartYear = parseInt(selectedYear.split('-')[0]);
  const currentYear = new Date().getFullYear();

  // Consider current FY if start year is current year or previous year
  const isCurrentFY = fyStartYear >= currentYear - 1;

  // Get date range for the financial year
  const dateRange = getFinancialYearDateRange(selectedYear);

  // For current FY, use current date; for historical FY, use end date
  const defaultToDate = isCurrentFY ? formatDateForAPI(new Date()) : dateRange.toDate;

  return {
    currentDateRange: {
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    },
    defaultToDate,
    isCurrentFY,
  };
}

// ================================================================
// INITIAL STATE SETUP
// ================================================================

/**
 * Get default financial year based on current date
 * Falls back to "2024-2025" if calculation fails
 */
function getDefaultFinancialYear(): string {
  try {
    const currentFY = getCurrentFinancialYear();
    return currentFY.label;
  } catch (error) {
    console.warn('Failed to get current financial year, using default:', error);
    return '2024-2025';
  }
}

/**
 * Initial state for financial year slice
 *
 * @constant initialState
 * @type {FinancialYearState}
 */
const initialState: FinancialYearState = (() => {
  const defaultYear = getDefaultFinancialYear();
  const calculatedData = calculateFinancialYearData(defaultYear);

  return {
    selectedFinancialYear: defaultYear,
    financialYear: extractFinancialYear(defaultYear),
    availableFinancialYears: generateFinancialYears(),
    ...calculatedData,
  };
})();

// ================================================================
// REDUX SLICE DEFINITION
// ================================================================

/**
 * Financial year Redux slice
 *
 * @slice financialYearSlice
 * @description Manages financial year state with actions for updates
 */
const financialYearSlice = createSlice({
  name: 'financialYear',
  initialState,
  reducers: {
    /**
     * Set the selected financial year
     * Updates both display and API formats, plus calculates date ranges
     *
     * @action setFinancialYear
     * @param {FinancialYearState} state - Current state
     * @param {PayloadAction<string>} action - Selected year in "YYYY-YYYY" format
     *
     * @example
     * ```typescript
     * dispatch(setFinancialYear('2023-2024'));
     * // Updates selectedFinancialYear to "2023-2024"
     * // Updates financialYear to "2023"
     * // Recalculates date ranges and default toDate
     * ```
     */
    setFinancialYear: (state, action: PayloadAction<string>) => {
      const selectedYear = action.payload;

      console.log('🔄 Redux financialYearSlice: setFinancialYear action', {
        currentSelectedYear: state.selectedFinancialYear,
        newSelectedYear: selectedYear,
        currentFinancialYear: state.financialYear,
        timestamp: new Date().toISOString(),
      });

      // Validate the selected year is in available years
      if (!state.availableFinancialYears.includes(selectedYear)) {
        console.warn(`Invalid financial year selected: ${selectedYear}`);
        return;
      }

      // Calculate new date data for the selected year
      const calculatedData = calculateFinancialYearData(selectedYear);

      // Update all state properties
      state.selectedFinancialYear = selectedYear;
      state.financialYear = extractFinancialYear(selectedYear);
      state.currentDateRange = calculatedData.currentDateRange;
      state.defaultToDate = calculatedData.defaultToDate;
      state.isCurrentFY = calculatedData.isCurrentFY;

      console.log('🔄 Redux financialYearSlice: State updated', {
        selectedFinancialYear: state.selectedFinancialYear,
        financialYear: state.financialYear,
        currentDateRange: state.currentDateRange,
        isCurrentFY: state.isCurrentFY,
      });

      // Optional: Persist to localStorage for user preference
      try {
        localStorage.setItem('selectedFinancialYear', selectedYear);
      } catch (error) {
        console.warn('Failed to persist financial year to localStorage:', error);
      }
    },

    /**
     * Reset financial year to default (current FY)
     *
     * @action resetFinancialYear
     * @param {FinancialYearState} state - Current state
     *
     * @example
     * ```typescript
     * dispatch(resetFinancialYear());
     * // Resets to current financial year with recalculated dates
     * ```
     */
    resetFinancialYear: (state) => {
      const defaultYear = getDefaultFinancialYear();
      const calculatedData = calculateFinancialYearData(defaultYear);

      state.selectedFinancialYear = defaultYear;
      state.financialYear = extractFinancialYear(defaultYear);
      state.currentDateRange = calculatedData.currentDateRange;
      state.defaultToDate = calculatedData.defaultToDate;
      state.isCurrentFY = calculatedData.isCurrentFY;

      // Clear localStorage preference
      try {
        localStorage.removeItem('selectedFinancialYear');
      } catch (error) {
        console.warn('Failed to clear financial year from localStorage:', error);
      }
    },

    /**
     * Initialize financial year from localStorage or default
     * Called during app initialization
     *
     * @action initializeFinancialYear
     * @param {FinancialYearState} state - Current state
     *
     * @example
     * ```typescript
     * dispatch(initializeFinancialYear());
     * // Loads persisted year or defaults to current FY
     * ```
     */
    initializeFinancialYear: (state) => {
      try {
        const persistedYear = localStorage.getItem('selectedFinancialYear');

        let yearToUse: string;
        if (persistedYear && state.availableFinancialYears.includes(persistedYear)) {
          yearToUse = persistedYear;
        } else {
          // Use default if no valid persisted year
          yearToUse = getDefaultFinancialYear();
        }

        // Calculate data for the selected year
        const calculatedData = calculateFinancialYearData(yearToUse);

        state.selectedFinancialYear = yearToUse;
        state.financialYear = extractFinancialYear(yearToUse);
        state.currentDateRange = calculatedData.currentDateRange;
        state.defaultToDate = calculatedData.defaultToDate;
        state.isCurrentFY = calculatedData.isCurrentFY;
      } catch (error) {
        console.warn('Failed to initialize financial year from localStorage:', error);
        // Fallback to default with full calculation
        const defaultYear = getDefaultFinancialYear();
        const calculatedData = calculateFinancialYearData(defaultYear);

        state.selectedFinancialYear = defaultYear;
        state.financialYear = extractFinancialYear(defaultYear);
        state.currentDateRange = calculatedData.currentDateRange;
        state.defaultToDate = calculatedData.defaultToDate;
        state.isCurrentFY = calculatedData.isCurrentFY;
      }
    },
  },
});

// ================================================================
// EXPORTS
// ================================================================

/**
 * Action creators for financial year management
 *
 * @exports actions
 * @property {ActionCreator} setFinancialYear - Set selected financial year
 * @property {ActionCreator} resetFinancialYear - Reset to default year
 * @property {ActionCreator} initializeFinancialYear - Initialize from storage
 */
export const { setFinancialYear, resetFinancialYear, initializeFinancialYear } =
  financialYearSlice.actions;

/**
 * Financial year reducer for store configuration
 *
 * @exports reducer
 */
export default financialYearSlice.reducer;

// ================================================================
// SELECTORS
// ================================================================

/**
 * Selector to get complete financial year state
 *
 * @selector selectFinancialYearState
 * @param {RootState} state - Redux root state
 * @returns {FinancialYearState} Complete financial year state
 */
export const selectFinancialYearState = (state: { financialYear: FinancialYearState }) =>
  state.financialYear;

/**
 * Selector to get selected financial year for display
 *
 * @selector selectSelectedFinancialYear
 * @param {RootState} state - Redux root state
 * @returns {string} Selected financial year in "YYYY-YYYY" format
 */
export const selectSelectedFinancialYear = (state: { financialYear: FinancialYearState }) =>
  state.financialYear.selectedFinancialYear;

/**
 * Selector to get financial year for API calls
 *
 * @selector selectFinancialYear
 * @param {RootState} state - Redux root state
 * @returns {string} Financial year in "YYYY" format for APIs
 */
export const selectFinancialYear = (state: { financialYear: FinancialYearState }) =>
  state.financialYear.financialYear;

/**
 * Selector to get available financial years
 *
 * @selector selectAvailableFinancialYears
 * @param {RootState} state - Redux root state
 * @returns {string[]} All available financial years
 */
export const selectAvailableFinancialYears = (state: { financialYear: FinancialYearState }) =>
  state.financialYear.availableFinancialYears;

/**
 * Selector to get current financial year date range
 *
 * @selector selectCurrentDateRange
 * @param {RootState} state - Redux root state
 * @returns {DateRange} Date range for selected financial year
 */
export const selectCurrentDateRange = (state: { financialYear: FinancialYearState }) =>
  state.financialYear.currentDateRange;

/**
 * Selector to get default toDate based on financial year type
 *
 * @selector selectDefaultToDate
 * @param {RootState} state - Redux root state
 * @returns {string} Default toDate (current date or FY end date)
 */
export const selectDefaultToDate = (state: { financialYear: FinancialYearState }) =>
  state.financialYear.defaultToDate;

/**
 * Selector to check if selected FY is current financial year
 *
 * @selector selectIsCurrentFY
 * @param {RootState} state - Redux root state
 * @returns {boolean} True if selected FY is current financial year
 */
export const selectIsCurrentFY = (state: { financialYear: FinancialYearState }) =>
  state.financialYear.isCurrentFY;

/**
 * ================================================================
 * END OF SLICE: Financial Year Management
 * ================================================================
 *
 * @summary          Redux slice for centralized financial year state management
 * @exports          Slice, actions, selectors for financial year functionality
 * @dependencies     Redux Toolkit, date utilities
 * @usedBy           useFinancialYear hook, header component, report pages
 *
 * @maintainers
 * - Primary: Development Team <dev@company.com>
 *
 * @changelog
 * - 2025-07-30: Initial implementation with Redux Toolkit
 *
 * @todos
 * - [ ] Add financial year validation
 * - [ ] Implement custom financial year ranges
 * - [ ] Add financial year change analytics
 *
 * @references
 * - Documentation: Financial Year Developer Guide
 * - Design: Financial Year System Architecture
 *
 * ================================================================
 */
